import React, { useState, useEffect } from "react";
import LoadingElement from "../loading-element/loading-element";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartPlus, faNoteSticky } from "@fortawesome/free-solid-svg-icons";
import { Dialog_VM, DialogFrame, useDialog } from "./dialog-base";
import { ApprovalDialog } from "./useApprovalDialogByRole";
import { InputWrapper } from "../Input/InputWrapper";
import { CustomInput } from "../Input/CustomInput";
import { InputError } from "../../screens/my-profile/InputError";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { Organization } from "../../../core/models/entities/Organization";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Profile } from "../../../core/models/entities/Profile";

const getCustomerFromEntity = (
  entity: any,
  profile?: Profile
): Organization | undefined => {
  const customer = entity?.customer as Organization;
  if (
    !(customer && profile) ||
    profile?.userOrganizationInformations.organizationId === customer.id
  )
    return undefined;
  return customer;
};

interface Props<T> {
  subject: string;
  onSkip: (entity: T) => Promise<void>;
  onOrder: (entity: T) => Promise<void>;
  onInitApproval: (entity: T, email?: string) => Promise<void>;
}

export function useApprovalDialogVendor<T>({
  subject,
  onSkip,
  onOrder: onSkipAndOrder,
  onInitApproval,
}: Props<T>): ApprovalDialog<T> {
  const initApproval = useDialog();
  const emailDialog = useDialog();
  const { profile } = useProfileContext();
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState<T>();

  function handleApproveWithExternal(email?: string) {
    setLoading(true);
    onInitApproval(entity!, email).finally(() => {
      setLoading(false);
      emailDialog.closeDialog();
    });
  }

  function cancel() {
    emailDialog.closeDialog();
    initApproval.openDialog();
  }

  function handleSkip() {
    setLoading(true);
    onSkip(entity!).finally(() => {
      setLoading(false);
      initApproval.closeDialog();
    });
  }

  function handleStartApproval() {
    initApproval.closeDialog();
    emailDialog.openDialog();
  }

  function handleSkipAndOrder() {
    setLoading(true);
    onSkipAndOrder(entity!).finally(() => {
      setLoading(false);
      initApproval.closeDialog();
    });
  }

  const email = getCustomerFromEntity(entity, profile)?.email;
  return {
    component: (
      <>
        <InitiateApprovalDialog2
          loading={loading}
          onSkip={handleSkip}
          onInitiateApproval={handleStartApproval}
          onSkipAndOrder={handleSkipAndOrder}
          subject={subject}
          dialogVm={initApproval}
        />
        <AddExternalApprover
          dialogVM={emailDialog}
          initEmail={email}
          onDone={handleApproveWithExternal}
          onCancel={cancel}
          loading={loading}
          subject={subject}
        />
      </>
    ),
    start: (entity) => {
      setEntity(entity);
      initApproval.openDialog();
    },
  };
}

interface AddExternalApproverProps {
  subject: string;
  dialogVM: Dialog_VM;
  loading: boolean;
  initEmail?: string;
  onDone: (email?: string) => void;
  onCancel: () => void;
}

interface InitiateApprovalDialog2_VM {
  onInitiateApproval: (e?: React.MouseEvent<HTMLElement>) => void;
  onSkipAndOrder: (e: React.MouseEvent<HTMLElement>) => void;
  onSkip: (e: React.MouseEvent<HTMLElement>) => void;
  subject: string;
  loading: boolean;
  dialogVm: Dialog_VM;
}

function InitiateApprovalDialog2({
  onInitiateApproval,
  subject,
  onSkipAndOrder,
  onSkip,
  loading,
  dialogVm,
}: InitiateApprovalDialog2_VM) {
  function handleSkip(e: React.MouseEvent<HTMLElement>) {
    if (loading) {
      return;
    }
    onSkip(e);
  }

  // Do not show dialogs with loading spinners.
  // Loading spinners are currently reserved for the base view only to avoid duplicates.
  if (loading) return null;

  return (
    <DialogFrame dialogOption={dialogVm}>
      {loading && <LoadingElement transparent />}
      <div className={"flex flex-col gap-4 p-4"}>
        <p className="capitalize text-left text-base font-bold">{subject}</p>
        <p>Click on an option to continue.</p>
        <div className={"flex justify-center space-x-4"}>
          <button
            className={
              "button-secondary w-48 uppercase align-items-center h-auto py-3 px-12 flex flex-col space-y-1"
            }
            onClick={onInitiateApproval}
            disabled={loading}
          >
            <FontAwesomeIcon size={"lg"} icon={faNoteSticky} />
            <span>Send Approval to Customer</span>
          </button>
          <button
            className={
              "button-secondary w-48 uppercase align-items-center h-auto py-3 px-12 flex flex-col space-y-1"
            }
            onClick={onSkipAndOrder}
            disabled={loading}
          >
            <FontAwesomeIcon size={"lg"} icon={faCartPlus} />
            <span>Add to order</span>
          </button>
        </div>
        <p
          className={"italic hover:underline cursor-pointer"}
          onClick={handleSkip}
        >
          Continue without ordering
        </p>
      </div>
    </DialogFrame>
  );
}

function AddExternalApprover({
  onDone,
  onCancel,
  loading,
  dialogVM,
  subject,
  initEmail,
}: AddExternalApproverProps) {
  const [email, setEmail] = useState<string>(initEmail ?? "");
  const [showError, setShowError] = useState<boolean>(false);
  const emailReg = new RegExp("^\\S+@\\S+$");

  useEffect(() => {
    if (initEmail) setEmail(initEmail);
  }, [initEmail]);

  function handleYes() {
    if (!email) {
      setShowError(true);
      return;
    }

    onDone(email);
  }

  function handleInput(input: string) {
    setShowError(!emailReg.test(input));
    setEmail(input);
  }

  if (loading) return null;

  return (
    <DialogFrame dialogOption={dialogVM}>
      {loading && <LoadingElement transparent />}
      <div className={"flex flex-col gap-4 p-4"}>
        <p className="capitalize text-left text-base font-bold">
          {subject} Approval
        </p>
        <p>Type the Email you wish to send the approval to.</p>
        <InputWrapper>
          <CustomInput
            className="bg-white"
            value={email}
            onChange={(e) => handleInput(e.target.value)}
          />
          {showError && (
            <InputError errorMessage={"Must be a valid email"}></InputError>
          )}
        </InputWrapper>
        <div className={"flex justify-center space-x-4"}>
          <SecondaryButton className="flex-1" onClick={onCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            className="flex-1"
            disabled={showError || loading}
            onClick={handleYes}
          >
            Send Approval
          </PrimaryButton>
        </div>
      </div>
    </DialogFrame>
  );
}
